// Adblock detection probed lazily on the first rewarded-ad click and cached
// for the session. We fetch a bait URL that ad-blocker filter lists target
// (Google's `show_ads.js`); if the request fails or doesn't return in time,
// we assume an ad-blocker, privacy browser, or network filter is in play.
//
// `mode: 'no-cors'` is required: the response is opaque (we never read it),
// but `fetch` still resolves on a real network success. Filter lists block
// the request before it leaves the browser, which produces a TypeError.

const BAIT_URL = 'https://pagead2.googlesyndication.com/pagead/show_ads.js';
const DETECTION_TIMEOUT_MS = 1500;

export type AdblockDetector = () => Promise<boolean>;

let cachedResult: boolean | null = null;
let inflight: Promise<boolean> | null = null;

export function detectAdblock(timeoutMs: number = DETECTION_TIMEOUT_MS): Promise<boolean> {
  if (cachedResult !== null) return Promise.resolve(cachedResult);
  if (inflight) return inflight;
  inflight = probe(timeoutMs).then((blocked) => {
    cachedResult = blocked;
    inflight = null;
    return blocked;
  });
  return inflight;
}

export function resetAdblockCacheForTests(): void {
  cachedResult = null;
  inflight = null;
}

async function probe(timeoutMs: number): Promise<boolean> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(BAIT_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return false;
  } catch {
    return true;
  } finally {
    clearTimeout(timer);
  }
}
